import { useEffect, useMemo, useRef, useState } from 'react';
import type { UIEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { RefreshCw, Search, Settings2 } from 'lucide-react';
import { feedService, taskService } from '@/core/api';
import { useTelegram, useStore } from '@/core/providers';
import { LoadingSpinner, EmptyState } from '@/components';
import FeedCard from './components/FeedCard';
import type { FeedResponse, FeedItem } from '@/types';

const PAGE_SIZE = 20;

const flattenPages = (pages?: FeedResponse[]) =>
  pages?.flatMap((page) => page.items) ?? [];

type ValueLevel = 'all' | 'important' | 'normal';

function FeedPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const { settings, isAuthenticated } = useStore();
  const [viewMode, setViewMode] = useState<'for-you' | 'my-tracking'>('for-you');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [valueLevel, setValueLevel] = useState<ValueLevel>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPullingUp, setIsPullingUp] = useState(false);
  const [pullUpDistance, setPullUpDistance] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number | null>(null);
  const allowPullUp = useRef(false);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      if (!isRefreshing) {
        // Silent refresh
        if (viewMode === 'my-tracking' && isAuthenticated) {
          personalFeedQuery.refetch();
        } else {
          globalFeedQuery.refetch();
        }
      }
    }, 60000); // 60 seconds

    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, isAuthenticated, isRefreshing]);

  const globalFeedQuery = useInfiniteQuery({
    queryKey: ['feed', 'global', settings.language, valueLevel],
    queryFn: ({ pageParam = 0 }) =>
      feedService.getFeed({
        limit: PAGE_SIZE,
        offset: pageParam,
        lang: settings.language,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const personalFeedQuery = useInfiniteQuery({
    queryKey: ['feed', 'personal', settings.language, selectedTaskId, valueLevel],
    queryFn: ({ pageParam = 0 }) =>
      feedService.getPersonalizedFeed({
        limit: PAGE_SIZE,
        offset: pageParam,
        lang: settings.language,
        task_id: selectedTaskId ?? undefined,
      }),
    initialPageParam: 0,
    enabled: isAuthenticated,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => taskService.getTasks(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const activeTasks = useMemo(
    () => (tasksQuery.data ?? []).filter((task) => task.status === 'active'),
    [tasksQuery.data]
  );

  useEffect(() => {
    if (selectedTaskId && !activeTasks.find((task) => task.id === selectedTaskId)) {
      setSelectedTaskId(null);
    }
  }, [selectedTaskId, activeTasks]);

  const showMyTracking = viewMode === 'my-tracking';
  const canViewPersonalFeed = isAuthenticated;

  const rawFeedItems = showMyTracking
    ? flattenPages(personalFeedQuery.data?.pages)
    : flattenPages(globalFeedQuery.data?.pages);

  // Apply value level filter
  const feedItems = useMemo(() => {
    if (valueLevel === 'all') return rawFeedItems;
    return rawFeedItems.filter(
      (item) => (item as any).importance_level?.toLowerCase() === valueLevel.toLowerCase()
    );
  }, [rawFeedItems, valueLevel]);

  const isActiveLoading = showMyTracking
    ? canViewPersonalFeed && personalFeedQuery.isLoading && !personalFeedQuery.data
    : globalFeedQuery.isLoading && !globalFeedQuery.data;

  const hasNextPage = showMyTracking
    ? canViewPersonalFeed && personalFeedQuery.hasNextPage
    : globalFeedQuery.hasNextPage;

  const isFetchingNext = showMyTracking
    ? personalFeedQuery.isFetchingNextPage
    : globalFeedQuery.isFetchingNextPage;

  const activeError = showMyTracking
    ? canViewPersonalFeed
      ? personalFeedQuery.error
      : null
    : globalFeedQuery.error;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    hapticFeedback('impact');
    const startTime = Date.now();
    try {
      if (showMyTracking) {
        if (canViewPersonalFeed) {
          await personalFeedQuery.refetch();
        }
      } else {
        await globalFeedQuery.refetch();
      }
    } finally {
      // Ensure minimum animation time of 1000ms
      const elapsed = Date.now() - startTime;
      if (elapsed < 1000) {
        await new Promise((resolve) => setTimeout(resolve, 1000 - elapsed));
      }
      setIsRefreshing(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;
    touchStartY.current = e.touches[0].clientY;
    setIsPullingUp(false);
    setPullUpDistance(0);
    allowPullUp.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartY.current === null || !scrollRef.current || isFetchingNext) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const distanceFromBottom = scrollHeight - clientHeight - scrollTop;
    if (distanceFromBottom <= 64) {
      allowPullUp.current = true; // re-arm every move near bottom
    }
    if (!allowPullUp.current) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta < 0) {
      const distance = Math.min(Math.abs(delta), 140);
      setIsPullingUp(true);
      setPullUpDistance(distance);
    } else {
      setIsPullingUp(false);
      setPullUpDistance(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isPullingUp) return;
    const threshold = 70;
    const shouldLoadMore = pullUpDistance >= threshold && hasNextPage && !isFetchingNext;
    setIsPullingUp(false);
    setPullUpDistance(0);
    touchStartY.current = null;
    allowPullUp.current = false;
    if (shouldLoadMore) {
      if (!showMyTracking) {
        void globalFeedQuery.fetchNextPage();
      } else if (canViewPersonalFeed) {
        void personalFeedQuery.fetchNextPage();
      }
    }
  };

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      if (!showMyTracking) {
        if (globalFeedQuery.hasNextPage && !globalFeedQuery.isFetchingNextPage) {
          globalFeedQuery.fetchNextPage();
        }
      } else if (canViewPersonalFeed) {
        if (personalFeedQuery.hasNextPage && !personalFeedQuery.isFetchingNextPage) {
          personalFeedQuery.fetchNextPage();
        }
      }
    }
  };

  const handleCardClick = (item: FeedItem) => {
    hapticFeedback('selection');
    navigate(`/feed/${item.id}`, { state: { item } });
  };

  const handleModeChange = (mode: 'for-you' | 'my-tracking') => {
    setViewMode(mode);
    hapticFeedback('selection');
  };

  const handleValueLevelChange = (level: ValueLevel) => {
    setValueLevel(level);
    hapticFeedback('selection');
  };

  const showLoginPrompt = showMyTracking && !canViewPersonalFeed;
  const showCreatePrompt =
    showMyTracking && canViewPersonalFeed && !tasksQuery.isLoading && activeTasks.length === 0;

  return (
    <div className="flex h-full flex-col">
      <div
        className="pointer-events-none fixed left-1/2 z-30 -translate-x-1/2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--ff-surface)]/90 px-2 py-1 shadow-[0_12px_28px_rgba(0,0,0,0.08)] backdrop-blur">
          <button
            onClick={() => navigate('/search')}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
            aria-label="Search"
          >
            <Search size={18} />
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
            aria-label="Settings"
          >
            <Settings2 size={18} />
          </button>
        </div>
      </div>

      <div className="space-y-3 px-4 pt-20">
        <div className="rounded-3xl border border-[var(--ff-border)] bg-[var(--ff-surface)] px-3 py-3 shadow-[0_12px_28px_rgba(0,0,0,0.06)] animate-soft-pop">
          <div className="flex flex-col gap-3">
            <div className="mx-auto flex w-full max-w-sm items-center rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] p-1">
              {([
                { label: 'For you', value: 'for-you' },
                { label: 'My tracking', value: 'my-tracking' },
              ] as const).map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => handleModeChange(value)}
                  className={`ios-pressable flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                    viewMode === value
                      ? 'bg-[var(--ff-surface)] text-telegram-text shadow-sm animate-soft-pop'
                      : 'text-telegram-hint'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2">
                {(['all', 'important', 'normal'] as ValueLevel[]).map((option) => (
                  <button
                    key={option}
                    onClick={() => handleValueLevelChange(option)}
                    className={`ios-pressable rounded-full border px-3 py-2 text-xs font-semibold capitalize transition-all duration-200 ${
                      valueLevel === option
                        ? 'border-[#0a84ff] bg-[var(--ff-surface)] text-[#0a84ff] shadow-sm animate-soft-pop'
                        : 'border-[var(--ff-border)] bg-[var(--ff-surface)] text-telegram-hint hover:bg-[var(--ff-surface-alt)]'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              <div className="ml-auto">
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="ios-pressable flex items-center gap-2 rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface)] px-4 py-2 text-sm font-semibold text-telegram-text shadow-sm transition hover:shadow"
                >
                  <RefreshCw className={isRefreshing ? 'animate-spin text-[#0a84ff]' : 'text-telegram-hint'} size={16} />
                  <span className="text-telegram-text">Refresh</span>
                </button>
              </div>
            </div>
          </div>

          {showMyTracking && canViewPersonalFeed && activeTasks.length > 0 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => {
                  hapticFeedback('selection');
                  setSelectedTaskId(null);
                }}
                className={`ios-pressable flex-shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                  selectedTaskId === null
                    ? 'border-[#0a84ff] bg-[#0a84ff] text-white shadow-sm animate-soft-pop'
                    : 'border-[var(--ff-border)] bg-[var(--ff-surface)] text-telegram-text hover:bg-[var(--ff-surface-alt)]'
                  }`}
                >
                  All tracking
                </button>
              {activeTasks.map((task) => (
                <button
                  key={task.id}
                  onClick={() => {
                    hapticFeedback('selection');
                    setSelectedTaskId(task.id);
                  }}
                  className={`ios-pressable flex-shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                    selectedTaskId === task.id
                      ? 'border-[#0a84ff] bg-[#0a84ff] text-white shadow-sm animate-soft-pop'
                      : 'border-[var(--ff-border)] bg-[var(--ff-surface)] text-telegram-text hover:bg-[var(--ff-surface-alt)]'
                  }`}
                >
                  {task.title || task.entity?.name || 'Untitled'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="space-y-4 px-4 py-4">
          {showLoginPrompt ? (
            <EmptyState
              title="Login required"
              description="Sign in to view your personalized tracking feed."
              action={
                <button onClick={() => navigate('/login')} className="btn-primary">
                  Go to Login
                </button>
              }
            />
          ) : showCreatePrompt ? (
            <EmptyState
              title="No tracking tasks yet"
              description="Create your first smart tracking request to build a personalized feed."
              action={
                <button onClick={() => navigate('/create')} className="btn-primary">
                  Create Task
                </button>
              }
            />
          ) : isActiveLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : activeError ? (
            <EmptyState
              title="Failed to load feed"
              description="Please check your connection and try again"
              action={
                <button onClick={handleRefresh} className="btn-primary">
                  Retry
                </button>
              }
            />
          ) : feedItems.length === 0 ? (
            <EmptyState
              title="No stories yet"
              description={
                showMyTracking
                  ? 'Tracked topics will show up once we find new signals.'
                  : 'New articles will appear here soon.'
              }
            />
          ) : (
            <>
              <div className="space-y-3">
                {feedItems.map((item) => (
                  <FeedCard key={item.id} item={item} onClick={() => handleCardClick(item)} />
                ))}
              </div>
              <div
                className="flex justify-center py-4 text-xs text-telegram-hint transition-transform duration-150 ease-out"
                style={{
                  transform:
                    isPullingUp && !isFetchingNext
                      ? `translateY(-${Math.min(pullUpDistance, 80) / 3}px)`
                      : 'translateY(0)',
                }}
              >
                {isFetchingNext ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>Loading more...</span>
                  </div>
                ) : hasNextPage ? (
                  <div
                    className="flex items-center gap-2 transition-colors"
                    style={{
                      color: isPullingUp ? '#0a84ff' : undefined,
                    }}
                  >
                    <RefreshCw className={isPullingUp ? 'text-[#0a84ff]' : 'text-telegram-hint'} size={14} />
                    <span>{isPullingUp ? 'Release to load more' : 'Pull up for more'}</span>
                  </div>
                ) : feedItems.length > 0 ? (
                  <span>You are all caught up ✨</span>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
}

export default FeedPage;
